<template>
    <section class="space-y-1">
        <form-label>{{ label }}</form-label>
        <p v-if="!items.length" class="text-xs text-gray-300">{{ empty }}</p>
        <template v-for="(item, index) in items" :key="item.type === 'choose' ? 'choose' : item.wire.id">
            <div v-if="item.type === 'action'" class="flex items-center gap-1 rounded border border-white/20 bg-white/5 px-1 py-0.5 text-xs">
                <RunEntry :page="page" :graph="graph" :wire="item.wire" />
                <MoveButtons :up="canMove(index, -1)" :down="canMove(index, 1)" @move="delta => move(index, delta)" />
                <button class="text-red-300 hover:text-red-200" title="Disconnect" @click="hass.removeWire(page, item.wire.id)">×</button>
            </div>
            <div v-else class="space-y-0.5 rounded border border-amber-400/40 bg-amber-400/5 px-1 py-0.5 text-xs">
                <div class="flex items-center gap-1">
                    <span class="flex-1 text-amber-200" title="Branches are tried in order; the first whose conditions hold runs">Choose (first matching branch)</span>
                    <MoveButtons v-if="btnmatrix" :up="canMove(index, -1)" :down="canMove(index, 1)" @move="delta => move(index, delta)" />
                </div>
                <div v-for="(branch, branchIndex) in item.branches" :key="branch.id" class="flex items-center gap-1 pl-2">
                    <RunEntry :page="page" :graph="graph" :wire="branch" />
                    <MoveButtons :up="branchIndex > 0" :down="branchIndex < item.branches.length - 1"
                        @move="delta => moveBranch(index, branchIndex, delta)" />
                    <button class="text-red-300 hover:text-red-200" title="Disconnect" @click="hass.removeWire(page, branch.id)">×</button>
                </div>
            </div>
        </template>
    </section>
</template>

<script setup>
import { computed, h } from 'vue';
import { useHassFlowStore } from '../../stores/hassFlowStore.js';
import { findNode } from '../../graph/graphModel.js';
import { flattenRunLayout, getTriggerRunLayout } from '../../hass/hassGraph.js';
import { describeHassNode } from '../../hass/hassPresentation.js';

/**
 * What a trigger or condition runs, in export order, with ↑/↓ to reorder.
 * A trigger's condition branches form one `choose` (see getTriggerRunLayout):
 * after its actions, or for a btnmatrix where its first branch is.
 */
const props = defineProps({
    page: { type: Object, required: true },
    graph: { type: Object, required: true },
    node: { type: Object, required: true },
    btnmatrix: { type: Boolean, default: false },
    label: { type: String, default: 'Runs (in order)' },
    empty: { type: String, default: 'Drag a wire from the output to an action.' },
});
const hass = useHassFlowStore();

const items = computed(() => getTriggerRunLayout(props.graph, props.node.id, { btnmatrix: props.btnmatrix }));

// Without btnmatrix the choose block always comes last, whatever the wires say.
const canMove = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= items.value.length) return false;
    return props.btnmatrix || (items.value[index].type === 'action' && items.value[target].type === 'action');
};
function commit(next) {
    hass.reorderRunWires(props.page, props.node.id, flattenRunLayout(next));
}
function move(index, delta) {
    const next = [...items.value];
    [next[index], next[index + delta]] = [next[index + delta], next[index]];
    commit(next);
}
function moveBranch(index, branchIndex, delta) {
    const branches = [...items.value[index].branches];
    [branches[branchIndex], branches[branchIndex + delta]] = [branches[branchIndex + delta], branches[branchIndex]];
    commit(items.value.map((item, at) => (at === index ? { ...item, branches } : item)));
}

const RunEntry = (entryProps) => {
    const target = findNode(entryProps.graph, entryProps.wire.to.node);
    const view = target ? describeHassNode(target) : { icon: '?', title: 'missing node', summary: '' };
    return h('button', {
        class: 'min-w-0 flex-1 truncate text-left hover:text-ui-active',
        title: view.summary,
        onClick: () => target && hass.select(entryProps.page, 'node', target.id),
    }, [`${view.icon} ${view.title} `, h('span', { class: 'text-gray-400' }, view.summary)]);
};
RunEntry.props = ['page', 'graph', 'wire'];

const MoveButtons = (buttonProps, { emit }) => [
    h('button', { class: 'px-0.5 disabled:opacity-30', title: 'Earlier', disabled: !buttonProps.up, onClick: () => emit('move', -1) }, '↑'),
    h('button', { class: 'px-0.5 disabled:opacity-30', title: 'Later', disabled: !buttonProps.down, onClick: () => emit('move', 1) }, '↓'),
];
MoveButtons.props = ['up', 'down'];
MoveButtons.emits = ['move'];
</script>
